import React, { useState } from "react";
import styles from "./ContactsTable.module.scss";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  EditIcon,
  RemoveIcon,
  SearchIcon,
  SuccessEditClose,
  SuccessTickEdit,
} from "customIcons";
import DeleteListItemModal from "@features/common/modal/deleteListItemModal/DeleteListItemModal";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAppDispatch } from "@redux/hooks";
import { notifyUser } from "@redux/slices/CommonSlice";

/* --------------------------------------------------------------------------
 * Types
 * -------------------------------------------------------------------------- */
export interface ContactRow {
  id: number;
  promotion_name: string;
  email: string;
  phone: string;
  success?: boolean;
  company?: string;
  country?: string;
}

interface ContactsTableProps {
  rows: any[];
  onRemove: (id: number) => void;
  onEditSave?: (id: number, updatedData: any) => void;
  excel?: boolean;
  isDeleting?: boolean;
  searchTerm?: string;
  onSearchChange?: (val: string) => void;
  onClearSearch?: () => void;
  isSearching?: boolean;
  isEditing?: boolean;
  onEditStateChange?: boolean;
  handleEditStateChange: (isEditing: boolean) => void;
}

/* --------------------------------------------------------------------------
 * Component
 * -------------------------------------------------------------------------- */
const ContactsTable: React.FC<ContactsTableProps> = ({
  rows,
  onRemove,
  isDeleting,
  onEditSave,
  excel,
  onSearchChange,
  searchTerm,
  isEditing,
  handleEditStateChange,
  onEditStateChange,
}) => {
  /* --------------------------------------------------------------------------
   * Column Width Map
   * -------------------------------------------------------------------------- */
  const colW = {
    id: 110,
    promotion: 180,
    name: 160,
    email: 240,
    mobile: 160,
    amount: 120,
    currency: 120,
    company: 200,
    country: 150,
    expiry_date: 200,
    language: 150,
    extra_fields: 240,
    action: 120,
  };

  /* --------------------------------------------------------------------------
   * Local State
   * -------------------------------------------------------------------------- */
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [selectedRow, setSelectedRow] = useState<ContactRow | null>(null);
  const [isLocalEditing, setIsLocalEditing] = useState(false);
  const dispatch = useAppDispatch();

  const displayedRows = rows || [];

  /* --------------------------------------------------------------------------
   * Utility: Normalize cell value to readable string
   * -------------------------------------------------------------------------- */
  const normalizeValue = (value: any): string => {
    if (value == null) return "";
    if (typeof value === "string" || typeof value === "number")
      return String(value);
    if (typeof value === "boolean") return value ? "true" : "false";
    if (
      typeof value === "object" &&
      "value" in value &&
      typeof value.value !== "object"
    ) {
      return normalizeValue(value.value);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const renderCellContent = (value: any) => (
    <span className={styles["saved-contacts__cell-text"]}>
      {normalizeValue(value)}
    </span>
  );

  /**
   * Renders cell content with error handling for empty fields
   * If field has error and is empty, shows red dash with tooltip
   */
  const renderCellWithError = (row: any, field: EditableField, value: any) => {
    const hasError = fieldHasError(row, field);
    const isEmpty = !value && value !== 0;

    // If field has error and is empty, show red dash with tooltip
    if (hasError && isEmpty) {
      return (
        <Tooltip
          arrow
          title={fieldTooltipTitle(row, field)}
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#000",
                color: "#fff",
                fontSize: "12px",
                padding: "10px",
                borderRadius: "8px",
              },
            },
            arrow: { sx: { color: "#000" } },
          }}
        >
          <span
            className={`${styles["error-text"]}`}
            style={{ fontSize: "20px", fontWeight: 600 }}
          >
            --
          </span>
        </Tooltip>
      );
    }

    // If field has error but has value, show value with error styling and tooltip
    if (hasError) {
      return (
        <Tooltip
          arrow
          title={fieldTooltipTitle(row, field)}
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#000",
                color: "#fff",
                fontSize: "12px",
                padding: "10px",
                borderRadius: "8px",
              },
            },
            arrow: { sx: { color: "#000" } },
          }}
        >
          <span>{normalizeValue(value)}</span>
        </Tooltip>
      );
    }

    // Normal rendering without error
    return renderCellContent(value);
  };

  /* --------------------------------------------------------------------------
   * Edit Handlers
   * -------------------------------------------------------------------------- */
  const startEdit = (row: any) => {
    setEditingRowId(row.id);
    handleEditStateChange(true);
    const toYMD = (d: any) =>
      d && dayjs(d).isValid() ? dayjs(d).format("YYYY-MM-DD") : "";
    const rawExtra = row.extra_fields?.value;
    const extra_fields =
      rawExtra && typeof rawExtra === "object"
        ? JSON.stringify(rawExtra)
        : typeof rawExtra === "string"
          ? rawExtra
          : "";
    setEditForm({
      email: row.email?.value || "",
      mobile: row.mobile?.value || "",
      company_name: row.company_name?.value || "",
      country: row.country?.value || "",
      name: row.name?.value || "",
      amount: row.amount?.value ?? "",
      currency: row.currency?.value || "",
      expiry_date: toYMD(row.expiry_date?.value),
      language: row.language?.value || "",
      extra_fields,
    });
  };

  const cancelEdit = () => {
    setEditingRowId(null);
    setEditForm({});
    handleEditStateChange(false);
  };

  const saveEdit = async () => {
    const { language, extra_fields, ...rest } = editForm;
    let extraToSend = extra_fields;
    if (extra_fields.trim()) {
      try {
        JSON.parse(extra_fields);
      } catch {
        // leave raw if invalid
      }
    }
    const payload = {
      ...rest,
      expiry_date: editForm.expiry_date
        ? dayjs(editForm.expiry_date).format("YYYY-MM-DD")
        : "",
      user_preferred_language: language,
      extra_fields: extraToSend,
    };
    setIsLocalEditing(true);

    try {
      // Wait for the parent's onEditSave to complete (including refetch)
      await onEditSave?.(editingRowId!, payload);

      dispatch(
        notifyUser({
          message: "Your changes have been successfully saved.",
          title: "Changes Saved",
        }),
      );

      // Only exit edit mode after everything is done
      setIsLocalEditing(false);
      handleEditStateChange(false);
      cancelEdit();
    } catch (error) {
      console.error("Edit failed:", error);
      setIsLocalEditing(false);
      // Don't cancel edit on error, let user try again
    }
  };

  /* --------------------------------------------------------------------------
   * Delete Modal Handlers
   * -------------------------------------------------------------------------- */
  const openDeleteModal = (id: number) => {
    setSelectedRow(rows.find((row) => row.id === id) || null);
    setDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedRow(null);
  };

  /* --------------------------------------------------------------------------
   * Error Helpers: Detection, message extraction, tooltip content
   * -------------------------------------------------------------------------- */
  type EditableField =
    | "email"
    | "mobile"
    | "company_name"
    | "country"
    | "name"
    | "amount"
    | "currency"
    | "expiry_date"
    | "language"
    | "extra_fields";

  /**
   * Check if a field has an error
   * The API returns errors in field objects: { error: "message", value: ... }
   */
  const fieldHasError = (row: any, field: EditableField) => {
    // For user_preferred_language, map to "language"
    const apiFieldName =
      field === "language" ? "user_preferred_language" : field;

    // Check if field object has an error property with a truthy value
    return !!row?.[apiFieldName]?.error;
  };

  /**
   * Get error message for a specific field
   * Returns the error message from the field's error property
   */
  const fieldErrorMessage = (row: any, field: EditableField): string => {
    // For user_preferred_language, map to "language"
    const apiFieldName =
      field === "language" ? "user_preferred_language" : field;

    return row?.[apiFieldName]?.error || "";
  };

  /**
   * Generate tooltip content for field error
   */
  const fieldTooltipTitle = (row: any, field: EditableField) => {
    const msg = fieldErrorMessage(row, field);
    if (!msg) return "";
    return (
      <div className={styles["error-tooltip"]}>
        <div className={styles["error-tooltip__heading"]}>
          Below error needs to be fixed
        </div>
        <div className={styles["error-tooltip__message"]}>{msg}</div>
      </div>
    );
  };

  /* --------------------------------------------------------------------------
   * Render
   * -------------------------------------------------------------------------- */
  return (
    <>
      <div className={styles["saved-contacts"]}>
        {/* Search Bar */}
        <div className={styles["saved-contacts__search-bar-container"]}>
          <div
            className={styles["saved-contacts__search-bar-container__inner"]}
          >
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by Email/Phone"
              className={
                styles["saved-contacts__search-bar-container__inner-input"]
              }
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
          </div>
        </div>

        {/* Table Wrapper */}
        <div
          className={`${styles["saved-contacts__table-wrapper"]} table-list ${
            excel ? styles["excel-wrapper"] : ""
          }`}
          style={{ overflowX: "auto" }}
        >
          <TableContainer
            className={styles["saved-contacts__scroll-container"]}
          >
            <Table
              className={styles["saved-contacts__table"]}
              sx={{ tableLayout: "fixed", minWidth: 1800 }}
            >
              <TableHead>
                <TableRow
                  className={styles["saved-contacts__head-row"]}
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 4,
                    backgroundColor: "#fff",
                  }}
                >
                  <TableCell
                    style={{
                      width: colW.id,
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                      position: "sticky",
                      overflow: "hidden",
                      left: 0,
                      zIndex: 5,
                      padding: 0,
                      backgroundColor: "#fff",
                    }}
                  >
                    <span className={styles["saved-contacts__reference"]}>
                      Reference No.
                    </span>
                  </TableCell>
                  <TableCell
                    style={{
                      width: colW.promotion,
                      fontWeight: 600,
                      position: "sticky",
                      left: colW.id,
                      boxShadow: "rgba(0, 0, 0, 0.03) 8px 0px 6px 0px",
                      zIndex: 5,
                    }}
                  >
                    Promotion Name
                  </TableCell>
                  <TableCell style={{ width: colW.name, fontWeight: 600 }}>
                    Name
                  </TableCell>
                  <TableCell style={{ width: colW.email, fontWeight: 600 }}>
                    Email
                  </TableCell>
                  <TableCell style={{ width: colW.mobile, fontWeight: 600 }}>
                    Mobile
                  </TableCell>
                  <TableCell style={{ width: colW.amount, fontWeight: 600 }}>
                    Amount
                  </TableCell>
                  <TableCell style={{ width: colW.currency, fontWeight: 600 }}>
                    Currency
                  </TableCell>
                  <TableCell style={{ width: colW.company, fontWeight: 600 }}>
                    Company
                  </TableCell>
                  <TableCell style={{ width: colW.country, fontWeight: 600 }}>
                    Country
                  </TableCell>
                  <TableCell
                    style={{ width: colW.expiry_date, fontWeight: 600 }}
                  >
                    Expiry Date
                  </TableCell>
                  <TableCell style={{ width: colW.language, fontWeight: 600 }}>
                    Language
                  </TableCell>
                  <TableCell
                    style={{ width: colW.extra_fields, fontWeight: 600 }}
                  >
                    Extra Fields
                  </TableCell>
                  <TableCell
                    style={{
                      width: colW.action,
                      fontWeight: 600,
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#fff",
                      zIndex: 5,
                      padding: 0,
                      boxShadow: "rgba(0, 0, 0, 0.03) -8px 0px 6px 0px",
                    }}
                    align="right"
                  >
                    <span className={styles["saved-contacts__action"]}>
                      Action
                    </span>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody className="editable-table-body">
                {displayedRows?.map((r) => (
                  <TableRow
                    key={r.id}
                    className={`${
                      excel
                        ? styles["saved-contacts__excel-row"]
                        : styles["saved-contacts__body-row"]
                    }`}
                    hover
                  >
                    {/* Reference No */}
                    <TableCell
                      style={{
                        width: colW.id,
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#fff",
                        zIndex: 2,
                      }}
                    >
                      {renderCellContent(r?.id?.toString())}
                    </TableCell>

                    {/* Promotion Name */}
                    <TableCell
                      style={{
                        width: colW.promotion,
                        position: "sticky",
                        left: colW.id,
                        backgroundColor: "#fff",
                        zIndex: 2,
                        boxShadow: "rgba(0, 0, 0, 0.03) 8px 0px 6px 0px",
                      }}
                    >
                      {renderCellContent(
                        r?.promotion?.name?.value ?? r?.promotion?.name,
                      )}
                    </TableCell>

                    {/* Name */}
                    <TableCell
                      className={
                        fieldHasError(r, "name") ? styles["error-text"] : ""
                      }
                      style={{ width: colW.name }}
                    >
                      {editingRowId === r.id ? (
                        <TextField
                          size="small"
                          value={editForm.name}
                          onChange={(e) =>
                            setEditForm((prev: any) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        renderCellWithError(r, "name", r?.name?.value)
                      )}
                    </TableCell>

                    {/* Email */}
                    <TableCell
                      className={
                        fieldHasError(r, "email") ? styles["error-text"] : ""
                      }
                      style={{ width: colW.email }}
                    >
                      {editingRowId === r.id ? (
                        <TextField
                          size="small"
                          value={editForm.email}
                          onChange={(e) =>
                            setEditForm((prev: any) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        renderCellWithError(r, "email", r?.email?.value)
                      )}
                    </TableCell>

                    {/* Mobile */}
                    <TableCell
                      className={
                        fieldHasError(r, "mobile") ? styles["error-text"] : ""
                      }
                      style={{ width: colW.mobile }}
                    >
                      {editingRowId === r.id ? (
                        <TextField
                          size="small"
                          value={editForm.mobile}
                          onChange={(e) =>
                            setEditForm((prev: any) => ({
                              ...prev,
                              mobile: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        renderCellWithError(r, "mobile", r?.mobile?.value)
                      )}
                    </TableCell>

                    {/* Amount */}
                    <TableCell
                      className={
                        fieldHasError(r, "amount") ? styles["error-text"] : ""
                      }
                      style={{ width: colW.amount }}
                    >
                      {editingRowId === r.id ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editForm.amount}
                          onChange={(e) =>
                            setEditForm((prev: any) => ({
                              ...prev,
                              amount:
                                e.target.value === ""
                                  ? ""
                                  : Number(e.target.value),
                            }))
                          }
                          InputProps={{
                            inputProps: {
                              min: 0,
                              step: 1,
                            },
                          }}
                          sx={{
                            "& input[type=number]": {
                              MozAppearance: "textfield",
                              "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button":
                                {
                                  WebkitAppearance: "auto",
                                  margin: 0,
                                  opacity: 1,
                                },
                            },
                          }}
                        />
                      ) : (
                        renderCellWithError(r, "amount", r?.amount?.value)
                      )}
                    </TableCell>

                    {/* Currency */}
                    <TableCell
                      className={
                        fieldHasError(r, "currency") ? styles["error-text"] : ""
                      }
                      style={{ width: colW.currency }}
                    >
                      {editingRowId === r.id ? (
                        <TextField
                          size="small"
                          value={editForm.currency}
                          onChange={(e) =>
                            setEditForm((prev: any) => ({
                              ...prev,
                              currency: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        renderCellWithError(r, "currency", r?.currency?.value)
                      )}
                    </TableCell>

                    {/* Company */}
                    <TableCell
                      className={
                        fieldHasError(r, "company_name")
                          ? styles["error-text"]
                          : ""
                      }
                      style={{ width: colW.company }}
                    >
                      {editingRowId === r.id ? (
                        <TextField
                          size="small"
                          value={editForm.company_name}
                          onChange={(e) =>
                            setEditForm((prev: any) => ({
                              ...prev,
                              company_name: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        renderCellWithError(
                          r,
                          "company_name",
                          r?.company_name?.value,
                        )
                      )}
                    </TableCell>

                    {/* Country */}
                    <TableCell
                      className={
                        fieldHasError(r, "country") ? styles["error-text"] : ""
                      }
                      style={{ width: colW.country }}
                    >
                      {editingRowId === r.id ? (
                        <TextField
                          size="small"
                          value={editForm.country}
                          onChange={(e) =>
                            setEditForm((prev: any) => ({
                              ...prev,
                              country: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        renderCellWithError(r, "country", r?.country?.value)
                      )}
                    </TableCell>

                    {/* Expiry Date */}
                    <TableCell
                      className={
                        editingRowId === r.id
                          ? ""
                          : fieldHasError(r, "expiry_date")
                            ? styles["error-text"]
                            : ""
                      }
                      style={{ width: colW.expiry_date }}
                    >
                      {editingRowId === r.id ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disablePast
                            minDate={dayjs().add(1, "day").startOf("day")}
                            value={
                              editForm.expiry_date
                                ? dayjs(editForm.expiry_date)
                                : null
                            }
                            onChange={(val: any) =>
                              setEditForm((prev: any) => ({
                                ...prev,
                                expiry_date: val
                                  ? dayjs(val).format("YYYY-MM-DD")
                                  : "",
                              }))
                            }
                            slotProps={{
                              textField: { size: "small", variant: "outlined" },
                            }}
                          />
                        </LocalizationProvider>
                      ) : (
                        renderCellWithError(
                          r,
                          "expiry_date",
                          r?.expiry_date?.value
                            ? dayjs(r.expiry_date.value).format("YYYY-MM-DD")
                            : "",
                        )
                      )}
                    </TableCell>

                    {/* Language */}
                    <TableCell
                      className={
                        fieldHasError(r, "language") ? styles["error-text"] : ""
                      }
                      style={{ width: colW.language }}
                    >
                      {editingRowId === r.id ? (
                        <TextField
                          size="small"
                          value={editForm.language}
                          onChange={(e) =>
                            setEditForm((prev: any) => ({
                              ...prev,
                              language: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        renderCellWithError(
                          r,
                          "language",
                          r?.user_preferred_language?.value,
                        )
                      )}
                    </TableCell>

                    {/* Extra Fields */}
                    <TableCell
                      className={
                        fieldHasError(r, "extra_fields")
                          ? styles["error-text"]
                          : ""
                      }
                      style={{ width: colW.extra_fields }}
                    >
                      {editingRowId === r.id ? (
                        <TextField
                          size="small"
                          value={editForm.extra_fields}
                          onChange={(e) =>
                            setEditForm((prev: any) => ({
                              ...prev,
                              extra_fields: e.target.value,
                            }))
                          }
                          multiline
                          minRows={1}
                          maxRows={4}
                          placeholder='e.g. {"test":"abc"}'
                        />
                      ) : (
                        renderCellWithError(
                          r,
                          "extra_fields",
                          r?.extra_fields?.value,
                        )
                      )}
                    </TableCell>

                    {/* Action */}
                    <TableCell
                      align="left"
                      style={{
                        width: colW.action,
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#fff",
                        zIndex: 2,
                        boxShadow: "rgba(0, 0, 0, 0.03) -8px 0px 6px 0px",
                      }}
                      className="credit-table-action"
                    >
                      <div className={styles["saved-contacts__action-cell"]}>
                        {editingRowId === r.id ? (
                          <>
                            <button
                              className={styles["saved-contacts__action-item"]}
                              onClick={saveEdit}
                              disabled={isEditing}
                            >
                              {isLocalEditing || isEditing ? (
                                <CircularProgress size={20} />
                              ) : (
                                <SuccessTickEdit />
                              )}
                            </button>
                            <button
                              className={styles["saved-contacts__action-item"]}
                              onClick={cancelEdit}
                              disabled={isEditing}
                            >
                              <SuccessEditClose />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className={styles["saved-contacts__action-item"]}
                              onClick={() => startEdit(r)}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className={styles["saved-contacts__action-item"]}
                              onClick={() => openDeleteModal(r.id)}
                            >
                              <RemoveIcon />
                            </button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Empty State */}
                {displayedRows?.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={14}
                      className={styles["saved-contacts__empty-cell"]}
                    >
                      No matches found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
              <DeleteListItemModal
                onClose={closeDeleteModal}
                selectedRow={selectedRow}
                onRemove={onRemove}
                isDeleting={isDeleting}
              />
            )}
          </TableContainer>
        </div>
      </div>
    </>
  );
};

export default ContactsTable;
